import { css } from "@emotion/css";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { KudosContributor } from "../../@types/KudosContributor";
import { PaginatedResponse } from "../../@types/PaginatedResponse";
import AddressList from "../../components/AddressList/AddressList";
import Button from "../../components/Button/Button";
import {
  TextH3,
  TextLink,
} from "../../components/DesignSystem/DesignSystemText";
import Image from "../../components/Image/Image";
import PageLoader from "../../components/Shared/PageLoader";
import { getChainIdFromEnv } from "../../config/env";
import { IKudosDetails, IKudosEndorser } from "../../services/apis";
import {
  createRequestTemplate,
  getRequestsForIdentity,
  getRequestTemplates,
} from "../../services/apis/KudosApi/RequestKudos";
import { useWallet } from "../../services/wallet/useWallet";
import { BASE_ASSET_URL } from "../../utils/asset";
import {
  generateOpenSeaLink,
  generateRaribleLink,
  openUrl,
} from "../../utils/links";
import { truncateAddress } from "../../utils/string";
import KudosPreview from "../CreateKudos/KudosPreview";
import { isDiscordKudos } from "../CreateKudos/utils";
import AdminSection from "./AdminSection";
import DetailsSection from "./DetailsSection";
import KudosEndorsements from "./KudosEndorsements";
import VerifiedIcon from "./VerifiedIcon";

interface Props {
  details?: IKudosDetails | null;
  isClaim?: boolean;
  endorsers?: IKudosEndorser[];
  owners?: KudosContributor[];
  ownersResponse?: PaginatedResponse;
  onShowMoreOwners: () => void;
  contributors?: KudosContributor[];
  contributorsResponse?: PaginatedResponse;
  onShowMoreContributors?: () => void;
  loading?: boolean;
}

const KudosDetailsCard = ({
  details,
  isClaim,
  contributors,
  contributorsResponse,
  onShowMoreContributors,
  ownersResponse,
  owners,
  onShowMoreOwners,
  endorsers,
  loading,
}: Props) => {
  const router = useRouter();
  const {
    username: walletUserName,
    isLoggedIn,
    isFeatureEnabled,
  } = useWallet();
  const [requestsForToken, setRequestsForToken] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestTemplate, setRequestTemplate] = useState<any>();

  const tokenId = useMemo(() => String(details?.tokenId), [details?.tokenId]);

  const isAllowlistRequired =
    details?.claimabilityAttributes?.isAllowlistRequired;

  const goToAddRecipients = () => {
    router.push(`/add-recipient/${tokenId}`);
  };

  const creatorUsername = details?.createdByAddress.username;
  const isVerified = details?.createdByAddress?.role === "admin";
  const isCreator = walletUserName === creatorUsername;
  const maxClaimCount = details?.claimabilityAttributes.totalClaimCount;
  const expirationTimestamp =
    details?.claimabilityAttributes.expirationTimestamp;

  const showAddRecipients = isAllowlistRequired && isCreator;

  const allowlist = contributors;

  const isDiscord = isDiscordKudos(contributors);

  const showSocialButtons = !!(owners?.length ?? 0 > 0);
  const openseaUrl = details?.tokenId
    ? generateOpenSeaLink(getChainIdFromEnv(), details?.tokenId)
    : "";
  const raribleUrl = details?.tokenId
    ? generateRaribleLink(getChainIdFromEnv(), details?.tokenId)
    : "";

  const showRequestButton = useMemo(
    () =>
      !loadingRequests &&
      isVerified &&
      isLoggedIn &&
      !requestsForToken.find(
        (request: any) =>
          request.tokenId === Number(tokenId) &&
          request.statusType === "approved",
      ) &&
      !!requestTemplate &&
      isFeatureEnabled("requestKudos"),
    [
      isVerified,
      isLoggedIn,
      requestsForToken,
      loadingRequests,
      requestTemplate,
    ],
  );

  const requestStatus = useMemo(() => {
    const request = requestsForToken.find(
      (request: any) => request.tokenId === Number(tokenId),
    );

    if (request?.statusType === "rejected") {
      return "Request denied";
    } else if (request?.statusType === "pending") {
      return "Request pending";
    }

    return "";
  }, [requestsForToken]);

  const onRequestKudosClick = () => {
    router.push(`/request/${details?.tokenId}`);
  };

  const createTemplate = async () => {
    await createRequestTemplate({
      tokenId: details?.tokenId as number,
      fields: fields,
      isEnabled: true,
    });
  };

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        if (details?.tokenId === undefined || !walletUserName) {
          return;
        }
        setLoadingRequests(true);
        const pendingRequests = await getRequestsForIdentity({
          username: walletUserName,
          tokenId: Number(tokenId),
          statusType: "pending",
        });
        const approvedRequests = await getRequestsForIdentity({
          username: walletUserName,
          tokenId: Number(tokenId),
          statusType: "approved",
        });
        const rejectedRequests = await getRequestsForIdentity({
          username: walletUserName,
          tokenId: Number(tokenId),
          statusType: "rejected",
        });
        const templateResponse = await getRequestTemplates({
          tokenId: tokenId,
          limit: 1,
        });

        setRequestTemplate(
          templateResponse?.data?.length && templateResponse?.data[0],
        );
        setRequestsForToken([
          ...pendingRequests?.data,
          ...approvedRequests?.data,
          ...rejectedRequests?.data,
        ]);
        setLoadingRequests(false);
      } catch (error: any) {
        setLoadingRequests(false);
        toast.error(error?.message);
        throw error;
      }
    };
    fetchRequests();
  }, [details?.tokenId, walletUserName]);
  return (
    <>
      <div className="w-full h-full p-12 shadow-media rounded-[32px] lg:h-[480px] lg:max-w-[944px] ">
        {loading ? (
          <div
            className={css`
              display: flex;
              width: 100%;
              justify-content: center;
              align-items: center;
            `}
          >
            <PageLoader />
          </div>
        ) : (
          <div className="w-full h-full overflow-y-scroll lg:flex lg:gap-x-10 lg:flex-row">
            <figure className="mb-[60rem]">
              <KudosPreview
                src={details?.imageUrl}
                className="shadow-none"
                priority
              />
            </figure>
            <aside className="overflow-scroll w-full flex flex-col gap-y-6 px-3">
              <TextH3 className="!text-[24px] !font-bold">
                {details?.headline}
              </TextH3>
              {/* <Button onClick={createTemplate}>Create Template</Button> */}

              {(showRequestButton || showSocialButtons) && (
                <div className="flex items-center gap-x-4">
                  {showRequestButton && (
                    <>
                      {requestStatus ? (
                        <Button disabled>{requestStatus}</Button>
                      ) : (
                        <Button onClick={onRequestKudosClick}>
                          Request Kudos
                        </Button>
                      )}
                    </>
                  )}

                  {showSocialButtons && (
                    <>
                      <Button
                        theme="white"
                        className="text-[#1a1a1a80] !p-2 !pr-4"
                        onClick={() => {
                          openUrl(openseaUrl);
                        }}
                      >
                        <Image
                          height={"24rem"}
                          width={"24rem"}
                          className="mr-2"
                          src={`${BASE_ASSET_URL}/icons/opensea.svg`}
                          alt=""
                        />
                        <span className="ml-2">Opensea</span>
                      </Button>
                      <Button
                        theme="white"
                        className="text-[#1a1a1a80] !p-2 !pr-4"
                        onClick={() => {
                          openUrl(raribleUrl);
                        }}
                      >
                        <Image
                          height={"24rem"}
                          width={"24rem"}
                          src={`${BASE_ASSET_URL}/icons/rarible.png`}
                          alt=""
                        />
                        <span className="ml-2">Rarible</span>
                      </Button>
                    </>
                  )}
                </div>
              )}

              {isCreator && !isClaim && (
                <AdminSection
                  uniqId={details?.uniqId}
                  totalClaimCount={maxClaimCount}
                  expirationTimestamp={expirationTimestamp}
                />
              )}

              <KudosEndorsements
                tokenId={String(details?.tokenId)}
                endorsers={endorsers}
              />

              <DetailsSection title="Description">
                {details?.description}
              </DetailsSection>
              <DetailsSection title="Community">
                <TextLink href={`/community/${details?.communityDisplayId}`}>
                  {details?.communityName}
                </TextLink>{" "}
              </DetailsSection>
              {details?.startDateTimestamp ? (
                <DetailsSection title="Dates">
                  {new Date(details?.startDateTimestamp).toLocaleDateString()}
                  {details?.endDateTimestamp
                    ? ` - ${new Date(
                        details.endDateTimestamp,
                      ).toLocaleDateString()}`
                    : ""}
                </DetailsSection>
              ) : (
                <></>
              )}
              <DetailsSection title="Links">
                {!!details?.links[0] && (
                  <ul>
                    {details.links?.map((link: any) => {
                      return (
                        <li key={link}>
                          <TextLink
                            href={
                              link.includes("http://") ||
                              link.includes("https://")
                                ? link
                                : "//" + link
                            }
                            target="_blank"
                          >
                            {link}
                          </TextLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </DetailsSection>
              <DetailsSection title="Creator">
                <div className="flex items-center">
                  <TextLink href={"/profile/" + creatorUsername}>
                    {truncateAddress(creatorUsername ?? "")}
                  </TextLink>

                  {isVerified && (
                    <span className="ml-2">
                      <VerifiedIcon />
                    </span>
                  )}
                </div>
              </DetailsSection>
              {owners?.length ? (
                <DetailsSection
                  title="Owners"
                  helperText={
                    isCreator && maxClaimCount && maxClaimCount >= 0
                      ? ` (${maxClaimCount} max)`
                      : undefined
                  }
                >
                  <AddressList
                    items={owners}
                    limit={ownersResponse?.limit}
                    count={ownersResponse?.count}
                    offset={ownersResponse?.offset}
                    onShowMore={onShowMoreOwners}
                  />
                </DetailsSection>
              ) : (
                <></>
              )}

              {isAllowlistRequired && (
                <DetailsSection
                  title="Allowlist"
                  action={
                    showAddRecipients && !isClaim && !isDiscord
                      ? { text: "Add", onClick: goToAddRecipients }
                      : undefined
                  }
                >
                  {allowlist?.length ? (
                    <AddressList
                      items={allowlist}
                      limit={contributorsResponse?.limit}
                      count={contributorsResponse?.count}
                      offset={contributorsResponse?.offset}
                      onShowMore={onShowMoreContributors}
                    />
                  ) : (
                    <>(All addresses have claimed)</>
                  )}
                </DetailsSection>
              )}
            </aside>
          </div>
        )}
      </div>
    </>
  );
};

KudosDetailsCard.defaultProps = {
  isClaim: false,
};

export default KudosDetailsCard;

const fields = [
  {
    type: "shortText",
    title: "What is your email?",
    required: true,
    description: "Lorem ipsum",
    referenceId: "234e4567-e89b-12d3-a456-42661417401",
  },
  {
    type: "shortText",
    title: "Please submit project info",
    required: true,
    description: "GitHub lik or smart contract address",
    referenceId: "234e4567-e89b-12d3-a456-42661417402",
  },
  {
    type: "longText",
    title: "What did you enjoy most about this event?",
    required: true,
    description: "Lorem ipsum",
    referenceId: "234e4567-e89b-12d3-a456-42661417403",
  },
];
